import http from '../../http-common'
import { stripId } from '@/services/utils'

type UpdateLeadStagePayload = {
  stage_id: number
  closure_reason_id?: number | null
  closure_notes?: string | null
}

class CrmDataService {
  getLeads(param: string): Promise<any> {
    const teste = param == undefined ? '' : param

    return http.get('/lead' + teste)
  }

  /**
   * Listar leads históricos (importados do WhatsApp Business App)
   * @param queryString Query string já montada (ex: 'search=Maria&page=1&page_size=20')
   */
  getHistoricalLeads(queryString = ''): Promise<any> {
    const qs = queryString ? (queryString.startsWith('?') ? queryString : `?${queryString}`) : ''
    return http.get('/lead/historical/' + qs)
  }

  getKanbanLeads(param: string): Promise<any> {
    const teste = param == undefined ? '' : param

    return http.get('/lead/getKanban' + teste)
  }

  getLeadByPhoneOrEmail(param: string): Promise<any> {
    return http.get('/lead/getByPhoneOrEmail/' + param)
  }

  getChatConfig(): Promise<any> {
    return http.get('/communication/chat-config/')
  }

  getTimeline(id: string | number, cursor?: string | null, signal?: AbortSignal): Promise<any> {
    return http.get(`/communication/${id}/timeline/`, { params: { cursor: cursor || undefined, limit: 30 }, signal })
  }

  getComms(id: string): Promise<any> {
    return http.get('/communication/' + id + '/by_lead/')
  }

  saveMessage(data: any): Promise<any> {
    return http.post('/communication/', data)
  }

  // DEPRECIADO: Método antigo - manter apenas para compatibilidade
  // Use downloadAttachment() para novos códigos
  getTheAttachment(param: any): Promise<any> {
    // Se param tem attachment_id, usar o novo endpoint
    if (param && param.attachment_id) {
      return http.get(`/integrations/media/${param.attachment_id}/`, {
        responseType: 'blob',
      })
    }

    // Fallback para o endpoint antigo (pode não existir mais)
    return http.post('/integrations/' + localStorage.getItem('empSlug') + '/getAttachmentFile/', param, {
      responseType: 'blob',
    })
  }

  readMessagesFromTheLead(param: string): Promise<any> {
    return http.post('/lead/readMessages/', param)
  }

  getVehicles(): Promise<any> {
    return http.get('/vehicle')
  }

  getPipelineStages(): Promise<any> {
    return http.get('/pipelinestage/getPipelineStages/')
  }

  getUsers(): Promise<any> {
    return http.get('/companyuser/' + localStorage.getItem('empSlug') + '/usersFromMyCompany')
  }

  updateTheLead(id: string, data: any): Promise<any> {
    const payload = stripId(data)
    return http.put('/lead/' + id + '/', payload)
  }

  patchLead(id: string, data: any): Promise<any> {
    const payload = stripId(data)
    return http.patch('/lead/' + id + '/', payload)
  }

  getIntegradorVehicles(): Promise<any> {
    return http.get('/integrador/get_vehicles')
  }

  getIntegradorVehicle(veiculo_id: string): Promise<any> {
    return http.get('/integrador/' + veiculo_id + '/get_vehicle/')
  }

  postVehicle(data: any): Promise<any> {
    return http.post('/vehicle/', data)
  }

  retryTheCommunication(comm_id: any): Promise<any> {
    return http.post('/communication/' + comm_id + '/retry/')
  }

  postLead(data: any): Promise<any> {
    return http.post('/lead/', data)
  }

  /**
   * Criação manual de Lead com regra de canal (1 lead aberto por contato por grupo de canal).
   * Endpoint: POST /lead/manual/
   *
   * Use este método para criar leads de canais offline ou administrativos
   * (ex.: phone_call, walk_in, referral, event, other), sem envio imediato
   * de mensagem via WhatsApp/E-mail.
   *
   * Em caso de conflito de unicidade, o backend retorna:
   *   - HTTP 409 com payload contendo pelo menos:
   *       { error_code: "LEAD_ALREADY_OPEN", lead_id, lead_source, detail }
   *
   * O frontend deve tratar 409 de forma semelhante a sendFirstMessage,
   * oferecendo uma navegação para o lead existente.
   */
  createManualLead(data: any): Promise<any> {
    return http.post('/lead/manual/', data)
  }

  /**
   * Cria ou reativa um Lead e envia a primeira mensagem.
   * Endpoint: POST /lead/sendFirstMessage/
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * FLUXOS QUE UTILIZAM ESTE MÉTODO:
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * 1. CHAT.VUE - Método handleFirstMessage()
   *    ─────────────────────────────────────────
   *    Localização: src/pages/crm/Chat.vue
   *    Trigger: Criação de novo room/lead diretamente no chat
   *    Comportamento:
   *      - Adiciona o novo room ao chatStore via chatStore.addRoom()
   *      - Exibe notificação de sucesso/erro
   *    Payload típico:
   *      { company, assigned_to, name, phone, source: 'whatsapp', template: {...} }
   *
   * 2. KANBAN.VUE - Método newRoom() → handleFirstMessage()
   *    ───────────────────────────────────────────────────────
   *    Localização: src/pages/crm/Kanban.vue
   *    Trigger: Botão "Adicionar Lead" no Kanban
   *    Comportamento:
   *      a) Busca leads existentes por telefone/email
   *      b) Se existe lead aberto:
   *         - Exibe SweetAlert perguntando se quer continuar ou criar novo
   *         - "Continuar": envia mensagem ao lead existente + navega ao chat
   *         - "Criar nova": abre modal de fechamento, após fechar chama handleFirstMessage()
   *      c) Se não existe: chama handleFirstMessage() diretamente
   *      d) Após sucesso: navega para o chat com o novo lead
   *    Payload:
   *      { company, assigned_to, name, phone/email, source, communication/template }
   *
   * 3. MODALCREATEOPPORTUNITY.VUE - Métodos handleLeadCreationLogic() e createNewLead()
   *    ──────────────────────────────────────────────────────────────────────────────────
   *    Localização: src/pages/contato/ModalCreateOpportunity.vue
   *    Trigger: Modal de criação de oportunidade a partir de um contato
   *    Comportamento:
   *      a) Converte telefone para formato E164 (prioriza phoneData.e164)
   *      b) Busca leads existentes (mesma lógica do Kanban)
   *      c) Se existe lead aberto:
   *         - Exibe SweetAlert perguntando se quer continuar ou criar novo
   *         - "Continuar": envia mensagem ao lead existente + fecha modal + navega ao chat
   *         - "Criar nova": abre modal de fechamento, após fechar chama createNewLead()
   *      d) Se não existe: chama createNewLead()
   *      e) createNewLead() chama sendFirstMessage(), fecha modal e emite evento 'roomCreated'
   *      f) Se props.redirectAfterCreate=true, navega ao chat
   *    Payload:
   *      { company, assigned_to, name, phone (E164), email, source, template/communication }
   *
   * 4. LEADTEST.VUE - Método sendFirstMessage() (Página de teste/debug)
   *    ─────────────────────────────────────────────────────────────────
   *    Localização: src/pages/demo/LeadTest.vue
   *    Trigger: Formulário de teste para desenvolvedores
   *    Comportamento:
   *      - Permite testar criação de leads via WhatsApp ou Email
   *      - Exibe resposta completa da API para debug
   *    Payload WhatsApp:
   *      { company, assigned_to, name, phone, source: 'whatsapp', template: { slug, text } }
   *    Payload Email:
   *      { company, assigned_to, name, email, source: 'other', communication }
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * CAMPOS DO PAYLOAD:
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Obrigatórios:
   *   - company: ID da empresa (localStorage.cmpId)
   *   - assigned_to: ID do CompanyUser responsável (localStorage.cmpU)
   *   - name: Nome do lead/contato
   *   - phone OU email: Identificador de contato (pelo menos um)
   *
   * Opcionais:
   *   - source: 'whatsapp' | 'instagram' | 'other' (default: inferido)
   *   - template: { slug, text } - Para WhatsApp, usa template aprovado
   *   - communication: string - Texto da mensagem (para email/outros)
   *   - close_if_open: boolean - Fecha lead anterior antes de criar novo
   *   - temporary_id: string - ID temporário para tracking otimista
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * RESPOSTAS DA API:
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Sucesso (200/201):
   *   - data: Lead object completo (id, name, phone, email, status, pipeline_stage, etc.)
   *
   * Erros comuns:
   *   - 400: Dados inválidos ou campos obrigatórios faltando
   *   - 404: Template não encontrado (para WhatsApp)
   *   - 409: Conflito (lead já existe e close_if_open não foi passado)
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * @param data Payload com dados do lead e mensagem inicial
   * @returns Promise com o Lead criado/reativado
   * @see docs/backend_references/crm.md para documentação completa do endpoint
   */
  sendFirstMessage(data: any): Promise<any> {
    return http.post('/lead/sendFirstMessage/', data)
  }

  closeLead(
    id: string,
    data: {
      closure_reason_id: number
      closure_notes?: string | null
    },
  ): Promise<any> {
    return http.post('/lead/' + id + '/closeLead/', data)
  }

  /**
   * Atualiza estágio do lead com suporte completo a motivos de fechamento
   * Endpoint principal para mudança de estágio com fechamento
   */
  updateLeadStage(id: string, stageId: number): Promise<any>
  updateLeadStage(id: string, data: UpdateLeadStagePayload): Promise<any>
  updateLeadStage(id: string, payload: number | UpdateLeadStagePayload): Promise<any> {
    const data: UpdateLeadStagePayload = typeof payload === 'number' ? { stage_id: payload } : payload

    // Debug logs
    if (import.meta.env.DEV) {
      console.group('🔍 [CrmService] updateLeadStage')
      console.log('Lead ID:', id)
      console.log('Payload keys:', Object.keys(data))
      console.log('URL:', '/lead/' + id + '/updateLeadStage/')
      console.groupEnd()
    }

    return http.post('/lead/' + id + '/updateLeadStage/', data)
  }

  getLeadClosureReasons(): Promise<any> {
    return http.get('/lead-closure-reasons/')
  }

  // Métodos para upload e download de anexos/mídia
  uploadWhatsAppMedia(file: File, leadId?: string | number): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('messaging_product', 'whatsapp')
    if (leadId) {
      formData.append('lead_id', String(leadId))
    }

    return http.post('/integrations/upload_media/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  downloadAttachment(attachmentId: string): Promise<any> {
    return http.get(`/integrations/media/${attachmentId}/`, {
      responseType: 'blob',
    })
  }

  // Métodos específicos para agenda/tarefas

  /**
   * Buscar leads para seleção em tarefas
   * @param params Query parameters de filtro
   */
  searchLeadsForTask(params: {
    search?: string
    company?: number
    pipeline_stage?: number
    assigned_to?: number
    temperature?: string
    source?: string
    page?: number
    page_size?: number
  }): Promise<any> {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString())
      }
    })

    return http.get(`/lead/?${queryParams.toString()}`)
  }

  /**
   * Obter lead específico por ID
   * @param id ID do lead
   */
  getLeadById(id: number): Promise<any> {
    return http.get(`/lead/${id}/`)
  }

  /**
   * Reativar lead histórico para o pipeline ativo
   * Backend move o lead para o primeiro estágio aberto e limpa closure_reason
   */
  reactivateHistoricalLead(leadId: string): Promise<any> {
    return http.post(`/lead/${leadId}/reactivate-historical/`)
  }

  /**
   * Obter painel de gerenciamento de leads com contagens e dados auxiliares
   * @param params Parâmetros opcionais
   */
  getLeadManagement(params: { lead_id?: number; limit?: number; search?: string } = {}): Promise<any> {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })

    const qs = queryParams.toString()
    return http.get(`/lead-management/${qs ? `?${qs}` : ''}`)
  }

  /**
   * Atribuir responsável a um lead
   * @param leadId ID do lead
   * @param userId ID do usuário
   */
  assignLead(leadId: string, userId: number): Promise<any> {
    return http.post(`/lead/${leadId}/assign/`, { user_id: userId })
  }

  /**
   * Buscar comunicações por lead
   * @param leadId ID do lead
   * @param params Parâmetros opcionais
   */
  getCommunicationsByLead(
    leadId: string,
    params: { channel?: string; direction?: string; page?: number; page_size?: number } = {},
  ): Promise<any> {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })

    const qs = queryParams.toString()
    return http.get(`/communication/${leadId}/by_lead/${qs ? `?${qs}` : ''}`)
  }

  /**
   * Criar nova comunicação (envio de mensagem)
   * @param data Dados da comunicação
   */
  createCommunication(data: any): Promise<any> {
    return http.post('/communication/', data)
  }

  /**
   * Listar usuários da empresa do usuário autenticado
   */
  getCompanyUsers(): Promise<any> {
    const empSlug = localStorage.getItem('empSlug')
    return http.get(`/companyuser/${empSlug}/usersFromMyCompany`)
  }

  /**
   * Mescla lead atual (primário, target) com outro lead fechado do mesmo contato e canal.
   * Endpoint: POST /lead/{id}/merge/
   */
  mergeLeads(target_id: string, data: any): Promise<any> {
    return http.post(`/lead/${target_id}/merge/`, data)
  }
}

export default new CrmDataService()
